import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { GlassButton, GlassCard, GlassOutlineButton } from '@components/common/Glass'
import { pollHeroActionSx } from '@components/common/PollDesign'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import {
  copyQuestion,
  getQuestionResponses,
  getQuestionResults,
  refreshQuestionRanking,
} from 'src/api/polls'
import type {
  Poll,
  QuestionResponsePage,
  QuestionResults,
} from 'src/api/types/polls'

const card = {
  p: { xs: 2, sm: 3 },
  borderColor: '#efddc9',
  boxShadow: '0 3px 0 #e8cdb2',
  textAlign: 'left',
}
const heading = { fontSize: 24, color: '#693413' }
type Props = {
  poll: Poll
  userId?: number
  sounds: { click: () => void }
  onUpdated: (poll: Poll) => void
  onPlay: (poll: Poll) => void
  onError: (message: string) => void
  onCopied?: (poll: Poll) => void
}

export function AskYourGroup({
  poll,
  userId,
  sounds,
  onError,
  onCopied,
}: Props) {
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  return (
    <>
      <GlassOutlineButton
        variant="outlined"
        color="secondary"
        sx={pollHeroActionSx}
        endIcon={<GroupsRoundedIcon sx={{ fontSize: 17 }} />}
        disabled={busy || (userId ? !poll.canCopyQuestion : false)}
        onClick={() => {
          sounds.click()
          setConfirm(true)
        }}
      >
        Ask your group
      </GlassOutlineButton>
      <Dialog
        open={confirm}
        onClose={() => !busy && setConfirm(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {userId ? 'Ask your group' : 'Log in to ask your group'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {userId
              ? 'Create a fresh private question with the same title and categories. No answers or results are copied. You can hide answers before sharing the new link.'
              : 'Create your own private copy and share it with friends or your team.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={busy}
            onClick={() => {
              sounds.click()
              setConfirm(false)
            }}
          >
            Cancel
          </Button>
          {!userId ? (
            <Button href="/login" onClick={sounds.click}>
              Log in
            </Button>
          ) : (
            <GlassButton
              disabled={busy}
              onClick={async () => {
                sounds.click()
                setBusy(true)
                try {
                  const result = await copyQuestion(poll.id!)
                  if (!mounted.current) return
                  if (!result.success || !result.data?.id)
                    throw new Error(
                      result.message || 'Could not copy this question.',
                    )
                  if (onCopied) onCopied(result.data)
                  else window.location.assign(`/polls/${result.data.id}`)
                } catch (error) {
                  if (mounted.current)
                    onError(
                      error instanceof Error
                        ? error.message
                        : 'Could not copy this question.',
                    )
                } finally {
                  if (mounted.current) {
                    setBusy(false)
                    setConfirm(false)
                  }
                }
              }}
            >
              {busy ? 'Creating…' : 'Create private question'}
            </GlassButton>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default function QuestionCommunityView({
  poll,
  userId,
  sounds,
  onUpdated,
  onPlay,
  onError,
}: Props) {
  const [page, setPage] = useState<QuestionResponsePage>()
  const [loading, setLoading] = useState(false)
  const [feedError, setFeedError] = useState('')
  const [results, setResults] = useState<QuestionResults>()
  const [resultsOpen, setResultsOpen] = useState(false)
  const [rankingLoading, setRankingLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const sequence = useRef(0)
  const rankingSequence = useRef(0)
  const load = async (cursor?: string) => {
    const request = ++sequence.current
    setLoading(true)
    setFeedError('')
    try {
      const response = await getQuestionResponses(poll.id!, cursor)
      if (request !== sequence.current) return
      if (!response.success || !response.data)
        throw new Error(response.message || 'Could not load responses.')
      setPage((previous) => ({
        ...response.data!,
        responses: cursor
          ? [
              ...(previous?.responses || []),
              ...response.data!.responses,
            ].filter(
              (row, index, all) =>
                all.findIndex((entry) => entry.id === row.id) === index,
            )
          : response.data!.responses,
      }))
    } catch (error) {
      if (request === sequence.current)
        setFeedError(
          error instanceof Error ? error.message : 'Could not load responses.',
        )
    } finally {
      if (request === sequence.current) setLoading(false)
    }
  }
  useEffect(() => {
    if (poll.canReadResponses) void load()
    else setPage(undefined)
    return () => {
      sequence.current++
    }
  }, [poll.id, poll.updatedAt, poll.submissions, poll.canReadResponses, userId])
  const reveal = async () => {
    sounds.click()
    if (resultsOpen) {
      setResultsOpen(false)
      return
    }
    setResultsOpen(true)
    setRankingLoading(true)
    const request = ++rankingSequence.current
    try {
      const response = await getQuestionResults(poll.id!)
      if (request !== rankingSequence.current) return
      if (!response.success || !response.data)
        throw new Error(response.message || 'Could not load this ranking.')
      setResults(response.data)
    } catch (error) {
      if (request === rankingSequence.current) {
        setResultsOpen(false)
        onError(
          error instanceof Error
            ? error.message
            : 'Could not load this ranking.',
        )
      }
    } finally {
      if (request === rankingSequence.current) setRankingLoading(false)
    }
  }
  useEffect(() => {
    rankingSequence.current++
    setRankingLoading(false)
    setResults(undefined)
    setResultsOpen(false)
    return () => {
      rankingSequence.current++
    }
  }, [poll.id, poll.rankingVersion, poll.canViewResults])
  const status = !poll.hasRanking
    ? ['queued', 'processing'].includes(poll.rankingStatus || '')
      ? 'Preparing the community ranking.'
      : 'Community ranking starts after 10 responses.'
    : poll.rankingStatus === 'failed'
      ? 'Showing the last published ranking.'
      : ['queued', 'processing'].includes(poll.rankingStatus || '')
        ? poll.answersClosedAt
          ? 'Updating final results. Showing the last published ranking until the final ranking is ready.'
          : 'Updating the community ranking. The last ranking is still available.'
        : poll.rankingStatus === 'dirty'
          ? 'New responses are waiting for the next ranking update.'
          : poll.answersClosedAt
            ? 'Submissions Closed. This ranking is final.'
            : 'The community ranking is ready. New answers are still welcome.'
  return (
    <>
      {poll.canReadResponses && (
        <GlassCard sx={card} className="rt-question-responses">
          <Stack spacing={1.5}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
            >
              <Typography variant="h3" sx={heading}>
                Recent Responses
              </Typography>
              <Button
                size="small"
                color="secondary"
                disabled={loading}
                onClick={() => {
                  sounds.click()
                  void load()
                }}
              >
                Refresh
              </Button>
            </Box>
            <Typography variant="body2" color="#855c3d">
              Everyone’s original words, newest first. Names or initials are
              optional and self-entered.
            </Typography>
            {loading &&
              !page &&
              Array.from({ length: 3 }, (_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={64}
                  sx={{ bgcolor: '#ffefdd' }}
                />
              ))}
            {page?.responses.map((response) => (
              <Box
                key={response.id}
                sx={{
                  p: 1.5,
                  border: '1px solid #efddc9',
                  borderRadius: '16px',
                  bgcolor: '#fffaf5',
                  overflowWrap: 'anywhere',
                }}
              >
                <Typography fontWeight={700} color="#693413">
                  {response.text}
                </Typography>
                <Box display="flex" gap={1} alignItems="center" mt={0.5}>
                  <Typography variant="caption" color="#855c3d">
                    {response.displayLabel || 'Anonymous'} ·{' '}
                    {new Date(response.updatedAt).toLocaleDateString()}
                  </Typography>
                  {response.hidden && (
                    <Chip size="small" label="Hidden · owner only" />
                  )}
                </Box>
              </Box>
            ))}
            {!loading && !feedError && !page?.responses.length && (
              <Typography color="#855c3d">
                Be the first to share your take.
              </Typography>
            )}
            {feedError && (
              <Typography role="alert" color="error">
                {feedError}
              </Typography>
            )}
            {page?.nextCursor && (
              <Button
                color="secondary"
                disabled={loading}
                onClick={() => {
                  sounds.click()
                  void load(page.nextCursor)
                }}
              >
                {loading ? 'Loading…' : 'Load More'}
              </Button>
            )}
          </Stack>
        </GlassCard>
      )}
      <GlassCard sx={card} className="rt-question-ranking">
        <Stack spacing={1.5}>
          <Typography variant="h3" sx={heading}>
            Community Ranking
          </Typography>
          <Typography variant="body2" color="#855c3d" role="status">
            {status}
          </Typography>
          {poll.rankingPublishedAt && poll.canViewResults && (
            <Typography variant="caption" color="#855c3d">
              Ranking {poll.rankingVersion} · {poll.rankedResponseCount || 0}{' '}
              responses at publication ·{' '}
              {new Date(poll.rankingPublishedAt).toLocaleString()}
            </Typography>
          )}
          {!poll.showSubmittedAnswers && !poll.answersClosedAt && (
            <Typography variant="body2" color="#855c3d">
              Answers are hidden until submissions close.{' '}
              {poll.creatorId === userId
                ? 'Only you can view the ranking; playing stays unavailable.'
                : 'Results and playing are unavailable.'}
            </Typography>
          )}
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              color="secondary"
              variant="outlined"
              disabled={!poll.canViewResults || rankingLoading}
              onClick={() => void reveal()}
            >
              {resultsOpen ? 'Hide Results' : 'View Results'}
            </Button>
            <GlassButton
              disabled={!poll.canPlay}
              onClick={() => {
                sounds.click()
                onPlay(poll)
              }}
            >
              Play
            </GlassButton>
            {poll.creatorId === userId && !poll.answersClosedAt && (
              <Button
                color="secondary"
                disabled={!poll.canRefreshRanking || refreshing}
                onClick={async () => {
                  sounds.click()
                  setRefreshing(true)
                  const response = await refreshQuestionRanking(poll.id!)
                  if (!mounted.current) return
                  setRefreshing(false)
                  if (response.success && response.data)
                    onUpdated(response.data)
                  else
                    onError(
                      response.message || 'Could not refresh this ranking.',
                    )
                }}
              >
                {refreshing ? 'Requesting…' : 'Refresh Ranking'}
              </Button>
            )}
          </Box>
          {!poll.canPlay && poll.hasRanking && (
            <Typography variant="caption" color="#855c3d">
              Needs 10 different answers to play.
            </Typography>
          )}
          {poll.creatorId === userId && !poll.answersClosedAt && (
            <Typography variant="caption" color="#855c3d">
              Manual refresh is available every five minutes.
              {poll.nextGrowthRefreshAt
                ? ` Automatic update at ${poll.nextGrowthRefreshAt} eligible responses.`
                : ''}
            </Typography>
          )}
          <Collapse in={resultsOpen && !!poll.canViewResults} unmountOnExit>
            <Stack spacing={1} pt={1}>
              {rankingLoading && (
                <Skeleton height={120} sx={{ bgcolor: '#ffefdd' }} />
              )}
              {results && (
                <>
                  <Typography variant="caption" color="#855c3d">
                    {results.eligibleResponses} responses in this ranking.
                    Similar answers are grouped together. Ties are ordered by
                    which answer was submitted first.
                    {results.method === 'deterministic'
                      ? ' Similar wording has not been AI-consolidated yet.'
                      : ''}
                  </Typography>
                  {results.entries.map((entry, i) => (
                    <Box
                      key={entry.groupId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: '16px',
                        bgcolor: '#fffaf5',
                        border: '1px solid #efddc9',
                      }}
                    >
                      <Typography fontWeight={800} color="#944217">
                        {i + 1}
                      </Typography>
                      <Typography
                        flex={1}
                        minWidth={0}
                        fontWeight={700}
                        color="#693413"
                        sx={{ overflowWrap: 'anywhere' }}
                      >
                        {entry.title}
                      </Typography>
                      <Box textAlign="right" flexShrink={0}>
                        <Typography fontWeight={700} color="#693413">
                          {entry.votes} {entry.votes === 1 ? 'vote' : 'votes'}
                        </Typography>
                        <Typography variant="caption" color="#855c3d">
                          {Math.round(entry.percentage)}%
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  {!results.entries.length && (
                    <Typography color="#855c3d">
                      No eligible responses.
                    </Typography>
                  )}
                </>
              )}
            </Stack>
          </Collapse>
        </Stack>
      </GlassCard>
    </>
  )
}
